import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare,
  Calendar,
  MessageSquare,
  Clock,
  Send,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Plus,
  Trash2,
  ArrowLeft,
  Fingerprint,
} from 'lucide-react';
import SelfAttendanceModal from '@/components/SelfAttendanceModal';
import { Task, taskService } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const StaffTasksPage = () => {
  const { session } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'P1' as 'P0' | 'P1' | 'P2',
    deadline: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    if (session?.userId) {
      loadTasks();
      const interval = setInterval(loadTasks, 10000);
      return () => clearInterval(interval);
    }
  }, [session]);

  const loadTasks = async () => {
    if (!session?.userId) return;
    try {
      const data = await taskService.getAll();
      const myTasks = data.filter(t =>
        t.assignedStaff?.includes(session.userId) ||
        t.assignedTo === session.userId
      );
      setTasks(myTasks);
    } catch (error) {
      console.error('Failed to load tasks', error);
    }
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'P0': return 'priority_high';
      case 'P1': return 'priority_medium';
      case 'P2': return 'priority_low';
      default: return 'secondary';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Pending': return 'status_pending';
      case 'In Progress': return 'status_progress';
      case 'Completed': return 'status_completed';
      default: return 'secondary';
    }
  };

  const handlePlatformToggle = async (taskId: string, platformIndex: number, currentStatus: string) => {
    const newStatus = currentStatus === 'Completed' ? 'Pending' : 'Completed';
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task || !task.platforms) return;

      const updatedPlatforms = task.platforms.map((p, idx) =>
        idx === platformIndex ? { ...p, status: newStatus as any } : p
      );

      const allCompleted = updatedPlatforms.every(p => p.status === 'Completed');
      const anyInProgress = updatedPlatforms.some(p => p.status !== 'Pending');
      const mainStatus = allCompleted ? 'Completed' : anyInProgress ? 'In Progress' : 'Pending';

      await taskService.update(taskId, { platforms: updatedPlatforms, status: mainStatus });
      toast({ title: `Marked as ${newStatus}`, duration: 1500 });
      loadTasks();
    } catch (error) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleAddComment = async () => {
    const task = tasks.find(t => t.id === expandedTaskId);
    if (!task || !comment.trim() || !session) return;

    try {
      await taskService.addComment(task.id, {
        taskId: task.id,
        authorId: session.userId,
        authorName: session.name,
        content: comment.trim(),
      });
      toast({ title: 'Comment added' });
      setComment('');
      loadTasks();
    } catch (error) {
      toast({ title: 'Failed to add comment', variant: 'destructive' });
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;

    try {
      await taskService.create({
        title: formData.title,
        description: formData.description,
        assignedStaff: [session.userId],
        createdBy: session.userId,
        createdByName: session.name,
        priority: formData.priority,
        deadline: formData.deadline,
        status: 'Pending',
      });

      toast({ title: 'Task created successfully' });
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', priority: 'P1', deadline: '' });
      loadTasks();
    } catch (error) {
      toast({ title: 'Failed to create task', variant: 'destructive' });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || !session) return;

    if (task.createdBy !== session.userId) {
      toast({ title: 'Permission denied', description: 'You can only delete your own tasks', variant: 'destructive' });
      return;
    }
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      await taskService.delete(taskId);
      toast({ title: 'Task deleted' });
      if (expandedTaskId === taskId) setExpandedTaskId(null);
      loadTasks();
    } catch (error) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'Pending').length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    completed: tasks.filter(t => t.status === 'Completed').length,
  };

  const getTaskDailyItems = (task: Task) => {
    if (!task.platforms || task.platforms.length === 0) return [];
    const items = task.platforms.map((p, idx) => ({
      platformName: p.name,
      date: p.startDate,
      amount: p.amount,
      times: p.times,
      status: p.status,
      platformIndex: idx,
    }));
    return items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const groupByDate = (items: ReturnType<typeof getTaskDailyItems>) => {
    return items.reduce((groups: Record<string, typeof items>, item) => {
      if (!groups[item.date]) groups[item.date] = [];
      groups[item.date].push(item);
      return groups;
    }, {});
  };

  const selectedTask = expandedTaskId ? tasks.find(t => t.id === expandedTaskId) : null;
  const dailyItems = selectedTask ? getTaskDailyItems(selectedTask) : [];
  const groupedByDate = groupByDate(dailyItems);

  const getTaskProgress = (task: Task) => {
    if (!task.platforms || task.platforms.length === 0) return null;
    const total = task.platforms.length;
    const done = task.platforms.filter(p => p.status === 'Completed').length;
    return { done, total, pct: Math.round((done / total) * 100) };
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  return (
    <div className="space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">My Tasks</h2>
          <p className="text-sm text-slate-500">Track and submit your daily execution reports.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="default"
            onClick={() => setIsAttendanceOpen(true)}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold border-slate-200 dark:border-slate-800"
          >
            <Fingerprint className="w-4.5 h-4.5" /> Biometric Check In/Out
          </Button>
          <Button
            variant="royal"
            size="default"
            onClick={() => setIsDialogOpen(true)}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold shadow-md shadow-primary/5"
          >
            <Plus className="w-4.5 h-4.5" /> Create Task
          </Button>
        </div>
      </div>

      {/* Modern Horizontal Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Tasks', value: stats.total, color: 'text-slate-900 dark:text-white', bg: 'bg-slate-100/50 dark:bg-slate-900/50' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/5 dark:bg-amber-500/10' },
          { label: 'In Progress', value: stats.inProgress, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/5 dark:bg-blue-500/10' },
          { label: 'Completed', value: stats.completed, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/5 dark:bg-emerald-500/10' },
        ].map(s => (
          <GlassCard key={s.label} className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
            <CardContent className={`p-4 text-center ${s.bg} rounded-2xl`}>
              <p className={`text-2xl font-black ${s.color} leading-none`}>{s.value}</p>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-2">{s.label}</p>
            </CardContent>
          </GlassCard>
        ))}
      </div>

      {/* Campaigns list & Detail views with transitions */}
      <AnimatePresence mode="wait">
        {!selectedTask ? (
          /* LIST: Campaigns overview grid */
          <motion.div
            key="list"
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, y: -15 }}
            className="space-y-4"
          >
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Active Campaigns</h3>
            {tasks.length === 0 ? (
              <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                <CardContent className="py-16 text-center">
                  <CheckSquare className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-4 animate-pulse" />
                  <h4 className="text-lg font-bold text-slate-850 dark:text-slate-100">No campaigns assigned</h4>
                  <p className="text-sm text-slate-500 max-w-xs mx-auto mt-1">You are currently fully cleared of any campaign work.</p>
                </CardContent>
              </GlassCard>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-[1600px]">
                {tasks.map(task => {
                  const progress = getTaskProgress(task);
                  return (
                    <motion.div
                      key={task.id}
                      variants={itemVariants}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <GlassCard
                        className="cursor-pointer hover:shadow-lg transition-all rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-700 text-white border-none shadow-md shadow-blue-500/10 min-h-[180px] flex flex-col justify-between"
                        onClick={() => setExpandedTaskId(task.id)}
                      >
                        <CardContent className="p-5 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex items-start justify-between gap-3">
                              <h4 className="font-bold text-base leading-snug line-clamp-2">
                                {task.clientName || task.title}
                              </h4>
                              <Badge variant="glass" className="bg-white/20 text-white border-none text-[9px] px-1.5 h-4.5 font-bold uppercase shrink-0">
                                {task.priority}
                              </Badge>
                            </div>
                            <p className="text-xs text-white/80 font-medium mt-1.5 line-clamp-1">
                              {task.campaignName}
                            </p>
                            <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider mt-1">
                              {task.year || ''} {task.location ? `• ${task.location}` : ''}
                            </p>
                          </div>

                          <div className="mt-6 space-y-3.5">
                            {progress && (
                              <div className="space-y-1">
                                <div className="flex justify-between items-center text-[10px] text-white/70 font-semibold">
                                  <span>Progress</span>
                                  <span>{progress.done}/{progress.total} Tasks</span>
                                </div>
                                <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-white transition-all duration-300"
                                    style={{ width: `${progress.pct}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="flex items-center justify-between border-t border-white/10 pt-3">
                              {task.createdAt && (
                                <span className="text-[9px] text-white/50 font-medium flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(task.createdAt).toLocaleDateString([], { dateStyle: 'short' })}
                                </span>
                              )}
                              <Badge className="bg-white text-indigo-700 border-none text-[9px] font-bold rounded-md px-1.5 py-0.5 shadow-sm">
                                {task.status}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </GlassCard>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        ) : (
          /* DETAIL: Checklists per campaign dates */
          <motion.div
            key="detail"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="space-y-5"
          >
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pb-2 border-b border-slate-200/50 dark:border-slate-800/40">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-250 dark:border-slate-800/60 font-semibold"
                  onClick={() => setExpandedTaskId(null)}
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to campaigns
                </Button>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{selectedTask.clientName || selectedTask.title}</h3>
                  <p className="text-xs text-slate-500 font-medium mt-0.5">
                    {selectedTask.campaignName} {selectedTask.year ? `(${selectedTask.year})` : ''} {selectedTask.location ? `• ${selectedTask.location}` : ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Date-wise Checklist */}
              <div className="lg:col-span-7 space-y-4">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Date-wise Execution Checklist</h4>
                {Object.entries(groupedByDate).map(([date, items]) => (
                  <div key={date} className="space-y-2.5">
                    <div className="flex items-center gap-2 px-1">
                      <Calendar className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-350">
                        {new Date(date + 'T00:00:00').toLocaleDateString(undefined, {
                          weekday: 'short', day: 'numeric', month: 'short'
                        })}
                      </span>
                      {date === new Date().toISOString().split('T')[0] && (
                        <Badge variant="royal" className="text-[9px] font-bold h-4.5 px-2 rounded-md">TODAY</Badge>
                      )}
                    </div>
                    {items.map(item => (
                      <GlassCard key={`${item.platformName}-${item.date}`} className="border-slate-200/50 dark:border-slate-850 shadow-sm hover:shadow-md transition-shadow bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                        <CardContent className="p-4 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3.5">
                            <Checkbox
                              id={`cb-${selectedTask.id}-idx-${item.platformIndex}`}
                              checked={item.status === 'Completed'}
                              className="w-5 h-5 rounded-md border-slate-300 dark:border-slate-750 text-indigo-600 focus:ring-indigo-500"
                              onCheckedChange={() =>
                                handlePlatformToggle(selectedTask.id, item.platformIndex, item.status)
                              }
                            />
                            <div>
                              <label
                                htmlFor={`cb-${selectedTask.id}-idx-${item.platformIndex}`}
                                className={`text-sm font-semibold select-none cursor-pointer ${
                                  item.status === 'Completed' ? 'line-through text-slate-400 dark:text-slate-500 font-medium' : 'text-slate-800 dark:text-slate-200'
                                }`}
                              >
                                {item.platformName}
                              </label>
                              {item.amount > 0 && (
                                <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium mt-0.5">₹{item.amount} Daily allowance</p>
                              )}
                              {item.times && item.times.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {item.times.map((t, ti) => (
                                    <Badge key={ti} variant="outline" className="text-[9px] font-semibold h-4.5 px-1.5 border-slate-200 dark:border-slate-800">
                                      {t}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <Badge variant={getStatusVariant(item.status) as any} className="text-[9px] font-bold rounded-lg px-2 py-0.5">
                            {item.status}
                          </Badge>
                        </CardContent>
                      </GlassCard>
                    ))}
                  </div>
                ))}
                {dailyItems.length === 0 && (
                  <div className="text-center py-10 border border-dashed border-slate-250 dark:border-slate-800 rounded-2xl bg-white/30 dark:bg-slate-900/30">
                    <p className="text-sm text-slate-400 font-medium">No campaign checklists configured for this campaign.</p>
                  </div>
                )}
              </div>

              {/* Campaign Info + Team Updates */}
              <div className="lg:col-span-5 space-y-4">
                {selectedTask.clientName && (
                  <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                    <CardHeader className="pb-3 pt-6 px-6">
                      <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-400">Campaign Details</CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6 grid grid-cols-2 gap-4 text-xs">
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase font-bold text-slate-400">Client Info</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200">{selectedTask.clientName}</p>
                        {selectedTask.clientWap && <p className="text-slate-500 font-medium">{selectedTask.clientWap}</p>}
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase font-bold text-slate-400">Campaign Metadata</p>
                        <p className="font-semibold text-slate-800 dark:text-slate-200">{selectedTask.campaignName}</p>
                        {selectedTask.location && <p className="text-slate-500 font-medium">{selectedTask.location}</p>}
                      </div>
                      {selectedTask.remarks && (
                        <div className="col-span-2 pt-3 border-t border-slate-100 dark:border-slate-800/50">
                          <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Remarks / Directives</p>
                          <p className="text-slate-650 dark:text-slate-350 leading-relaxed font-medium">{selectedTask.remarks}</p>
                        </div>
                      )}
                    </CardContent>
                  </GlassCard>
                )}

                {/* Self deletion if own custom task */}
                {session && selectedTask.createdBy === session.userId && (
                  <Button
                    variant="destructive"
                    size="default"
                    className="w-full rounded-xl font-bold"
                    onClick={() => handleDeleteTask(selectedTask.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Self-Created Task
                  </Button>
                )}

                {/* Team Updates / Comments */}
                <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
                  <CardHeader className="pb-3 pt-6 px-6">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                      <MessageSquare className="w-4.5 h-4.5 text-indigo-500" /> Activity Updates ({selectedTask.comments?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-6 pb-6 space-y-4">
                    <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1 scrollbar-thin">
                      {selectedTask.comments?.length === 0 ? (
                        <p className="text-xs text-slate-450 dark:text-slate-500 text-center py-6 font-medium">No updates reported. Submit progress updates below.</p>
                      ) : (
                        selectedTask.comments?.map(c => (
                          <div key={c.id} className="p-3 rounded-2xl bg-slate-100/50 dark:bg-slate-950/40 border border-slate-200/20 text-xs space-y-1">
                            <div className="flex items-center justify-between font-bold text-slate-700 dark:text-slate-300">
                              <span>{c.authorName}</span>
                              <span className="text-[10px] text-slate-400 font-medium">{new Date(c.createdAt).toLocaleDateString()}</span>
                            </div>
                            <p className="text-slate-600 dark:text-slate-350 leading-relaxed font-medium">{c.content}</p>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Textarea
                        placeholder="Type an update or report issue..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        className="flex-1 text-xs rounded-xl bg-slate-100/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-750 resize-none font-medium"
                      />
                      <Button
                        variant="royal"
                        size="icon"
                        className="rounded-xl w-10 h-10 shrink-0"
                        onClick={handleAddComment}
                        disabled={!comment.trim()}
                      >
                        <Send className="w-4.5 h-4.5" />
                      </Button>
                    </div>
                  </CardContent>
                </GlassCard>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Task Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Create Personal Task</DialogTitle>
            <DialogDescription className="text-sm">Create and track tasks on your personal board.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Task Title</label>
              <Input required placeholder="E.g., Field campaign banner audit"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Description</label>
              <Textarea required placeholder="Describe task details, objectives..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl border-slate-200 dark:border-slate-800"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Priority</label>
                <Select value={formData.priority}
                  onValueChange={(v: 'P0' | 'P1' | 'P2') => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="P0">P0 (High)</SelectItem>
                    <SelectItem value="P1">P1 (Medium)</SelectItem>
                    <SelectItem value="P2">P2 (Low)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Deadline</label>
                <Input type="date" required value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl">Cancel</Button>
              <Button type="submit" variant="royal" className="rounded-xl">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Biometric Self-Attendance Modal */}
      <SelfAttendanceModal
        open={isAttendanceOpen}
        onClose={() => setIsAttendanceOpen(false)}
      />
    </div>
  );
};

export default StaffTasksPage;
